import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventIcon from '@mui/icons-material/Event'
import InboxIcon from '@mui/icons-material/Inbox'
import PersonIcon from '@mui/icons-material/Person'
import SwapCallsIcon from '@mui/icons-material/SwapCalls'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'

import API from '../../../services/API'

const getShiftTitle = (shift) =>
  shift.event?.title ?? shift.shiftType ?? 'Shift swap request'

const getShiftDestination = (shift) =>
  `/global-roster?date=${moment(shift.start).format('YYYY-MM-DD')}&shift=${
    shift._id
  }`

const ShiftSwapRequests = () => {
  const [shifts, setShifts] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const theme = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    setFetchError(false)

    API.get('/shift-swap-requests')
      .then((res) => {
        if (res.status === 200) {
          setShifts(res.data ?? [])
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const sortedShifts = useMemo(
    () =>
      [...shifts].sort(
        (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf(),
      ),
    [shifts],
  )

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={1.5}
      >
        <Stack gap={0.5}>
          <Stack direction="row" gap={1} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.anGreen.main, 0.12),
                color: 'anGreen.main',
              }}
            >
              <SwapCallsIcon />
            </Box>
            <Typography variant="title2">Shift Swap Requests</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Loading requests'
              : `${sortedShifts.length} active request${sortedShifts.length === 1 ? '' : 's'}`}
          </Typography>
        </Stack>
        <Chip
          icon={<SwapCallsIcon />}
          label={`${sortedShifts.length} open`}
          sx={{
            alignSelf: { xs: 'flex-start', md: 'center' },
            borderRadius: 1,
            bgcolor: alpha(theme.palette.anGreen.main, 0.1),
            color: 'anGreen.main',
            border: '1px solid',
            borderColor: alpha(theme.palette.anGreen.main, 0.24),
            '& .MuiChip-icon': { color: 'anGreen.main' },
          }}
        />
      </Stack>

      {fetchError && (
        <Alert severity="error">Unable to load shift swap requests.</Alert>
      )}

      {loading && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CircularProgress color="anGreen" />
        </Paper>
      )}

      {!loading && !fetchError && sortedShifts.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.04),
            borderColor: alpha(theme.palette.success.main, 0.22),
          }}
        >
          <Stack gap={1} alignItems="center">
            <InboxIcon color="success" />
            <Typography fontWeight={800}>No active swap requests</Typography>
            <Typography variant="body2" color="text.secondary">
              All caught up.
            </Typography>
          </Stack>
        </Paper>
      )}

      {!loading && !fetchError && sortedShifts.length > 0 && (
        <Stack gap={1}>
          {sortedShifts.map((shift) => {
            const userAccount = shift?.assignedTo?.userAccount ?? {}
            const staffName =
              `${userAccount.firstName ?? ''} ${userAccount.lastName ?? ''}`.trim() ||
              'Assigned staff'

            return (
              <Paper
                key={shift._id}
                variant="outlined"
                onClick={() => navigate(getShiftDestination(shift))}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  borderColor: alpha(theme.palette.anGreen.main, 0.25),
                  transition:
                    'border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
                  '&:hover': {
                    borderColor: 'anGreen.main',
                    boxShadow: `0 10px 26px ${alpha(
                      theme.palette.anGreen.main,
                      0.14,
                    )}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Avatar
                    src={userAccount.photograph}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.anGreen.main, 0.12),
                      color: 'anGreen.main',
                    }}
                  >
                    <PersonIcon />
                  </Avatar>

                  <Stack gap={0.75} flex={1} minWidth={0}>
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography fontWeight={800} noWrap>
                        {getShiftTitle(shift)}
                      </Typography>
                      {shift.clinicalRole && (
                        <Chip
                          size="small"
                          label={shift.clinicalRole}
                          sx={{ borderRadius: 1 }}
                        />
                      )}
                    </Stack>

                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        icon={<PersonIcon />}
                        label={staffName}
                        sx={{ borderRadius: 1 }}
                      />
                      <Chip
                        size="small"
                        icon={<CalendarMonthIcon />}
                        label={moment(shift.start).format('ddd Do MMM')}
                        sx={{ borderRadius: 1 }}
                      />
                      <Chip
                        size="small"
                        icon={<AccessTimeIcon />}
                        label={`${moment(shift.start).format('HH:mm')}-${moment(
                          shift.end,
                        ).format('HH:mm')}`}
                        sx={{ borderRadius: 1 }}
                      />
                      <Chip
                        size="small"
                        icon={<EventIcon />}
                        label={shift.event?._id ? 'Event' : 'Roster'}
                        sx={{ borderRadius: 1 }}
                      />
                    </Stack>
                  </Stack>

                  <IconButton
                    aria-label="Open shift swap request"
                    color="anGreen"
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

export default ShiftSwapRequests
